import React, { useState, useEffect, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Local images for gallery
const STARCRAFT_IMAGES = [
  { id: 1, src: "/images/starcraft/StarPic1.png", alt: "Starcraft Exterior" },
  { id: 2, src: "/images/starcraft/StarPic2.png", alt: "Starcraft Interior Living" },
  { id: 3, src: "/images/starcraft/StarPic3.png", alt: "Starcraft Bedroom" },
  { id: 4, src: "/images/starcraft/StarPic4.png", alt: "Starcraft Kitchen" },
  { id: 5, src: "/images/starcraft/StarPic5.png", alt: "Starcraft Bunk Area" },
];

const AMERILITE_IMAGES = [
  { id: 1, src: "/images/amerilite/AmerPic1.png", alt: "Ameri-lite Exterior" },
  { id: 2, src: "/images/amerilite/AmerPic2.png", alt: "Ameri-lite Interior" },
  { id: 3, src: "/images/amerilite/AmerPic3.png", alt: "Ameri-lite Living Space" },
  { id: 4, src: "/images/amerilite/AmerPic4.png", alt: "Ameri-lite Setup" },
  { id: 5, src: "/images/amerilite/AmerPic5.png", alt: "Ameri-lite View" },
];

// Photo Gallery Component
const PhotoGallery = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const nextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div className="photo-gallery" data-testid={`gallery-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="gallery-main">
        <button className="gallery-nav prev" onClick={prevImage} data-testid="gallery-prev">
          <i className="fas fa-chevron-left"></i>
        </button>
        <img
          src={images[currentIndex].src}
          alt={images[currentIndex].alt}
          className="gallery-main-image"
          onClick={() => openLightbox(currentIndex)}
          data-testid="gallery-main-image"
        />
        <button className="gallery-nav next" onClick={nextImage} data-testid="gallery-next">
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>
      <div className="gallery-thumbnails">
        {images.map((img, index) => (
          <img
            key={img.id}
            src={img.src}
            alt={img.alt}
            className={`gallery-thumbnail ${index === currentIndex ? 'active' : ''}`}
            onClick={() => setCurrentIndex(index)}
            data-testid={`gallery-thumbnail-${index}`}
          />
        ))}
      </div>
      
      {lightboxOpen && (
        <div className="lightbox" onClick={() => setLightboxOpen(false)} data-testid="lightbox">
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxOpen(false)}>
              <i className="fas fa-times"></i>
            </button>
            <button className="lightbox-nav prev" onClick={prevImage}>
              <i className="fas fa-chevron-left"></i>
            </button>
            <img src={images[currentIndex].src} alt={images[currentIndex].alt} />
            <button className="lightbox-nav next" onClick={nextImage}>
              <i className="fas fa-chevron-right"></i>
            </button>
            <div className="lightbox-counter">{currentIndex + 1} / {images.length}</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Booking Modal Component
const BookingModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    trailer: 'starcraft',
    start_date: '',
    end_date: '',
    terms_accepted: false,
    signature: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [conflictMessage, setConflictMessage] = useState('');
  const [bookingId, setBookingId] = useState('');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
    setConflictMessage('');
  };

  const checkAvailability = async () => {
    if (!formData.trailer || !formData.start_date || !formData.end_date) return;
    
    try {
      const response = await axios.post(`${API}/bookings/check-conflict`, {
        trailer: formData.trailer,
        start_date: formData.start_date,
        end_date: formData.end_date
      });
      
      if (response.data.has_conflict) {
        setConflictMessage(response.data.message);
      } else {
        setConflictMessage('');
      }
    } catch (err) {
      console.error('Error checking availability:', err);
    }
  };

  useEffect(() => {
    if (formData.start_date && formData.end_date && formData.trailer) {
      checkAvailability();
    }
  }, [formData.start_date, formData.end_date, formData.trailer]);

  // Signature pad functions
  const startDrawing = (e) => {
    e.preventDefault(); // Prevent scrolling on touch
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault(); // Prevent scrolling on touch
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault(); // Prevent scrolling on touch
    if (isDrawing) {
      const canvas = canvasRef.current;
      setFormData(prev => ({ ...prev, signature: canvas.toDataURL() }));
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setFormData(prev => ({ ...prev, signature: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!formData.terms_accepted) {
      setError('You must accept the rental terms to proceed.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/bookings`, formData);
      setSuccess(true);
      setBookingId(response.data.id);
    } catch (err) {
      const errorDetail = err.response?.data?.detail || 'An error occurred. Please try again.';
      
      // Parse error type from message
      if (errorDetail.includes('ERROR_DATE_CONFLICT') || err.response?.status === 409) {
        // Remove the error code prefix for display
        const cleanMessage = errorDetail.replace('ERROR_DATE_CONFLICT: ', '');
        setConflictMessage(cleanMessage);
      } else if (errorDetail.includes('ERROR_SATURDAY_PICKUP') || errorDetail.includes('ERROR_SUNDAY_PICKUP')) {
        const cleanMessage = errorDetail.replace(/ERROR_\w+: /, '');
        setError(cleanMessage);
      } else if (errorDetail.includes('ERROR_')) {
        // Remove error code prefix for cleaner display
        const cleanMessage = errorDetail.replace(/ERROR_\w+: /, '');
        setError(cleanMessage);
      } else {
        setError(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="booking-modal">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} data-testid="modal-close">
          <i className="fas fa-times"></i>
        </button>
        
        {success ? (
          <div className="booking-success" data-testid="booking-success">
            <i className="fas fa-check-circle success-icon"></i>
            <h2>Booking Request Submitted!</h2>
            <p>Your booking reference: <strong>{bookingId.substring(0, 8)}</strong></p>
            <p>To complete your reservation, please submit your $500 deposit:</p>
            <a
              href="https://square.link/u/xVaxRD0b"
              target="_blank"
              rel="noopener noreferrer"
              className="deposit-button"
              data-testid="deposit-button"
            >
              <i className="fas fa-credit-card"></i> Submit Deposit ($500)
            </a>
            <p className="deposit-note">
              Your deposit will convert to your damage deposit at pickup.
              You will receive a confirmation email with all details.
            </p>
          </div>
        ) : (
          <>
            <h2><i className="fas fa-calendar-check"></i> Book Your Rental</h2>
            
            {error && <div className="error-message" data-testid="error-message"><i className="fas fa-exclamation-circle"></i> {error}</div>}
            {conflictMessage && (
              <div className="conflict-message" data-testid="conflict-message">
                <i className="fas fa-exclamation-triangle"></i> {conflictMessage}
              </div>
            )}
            
            <form onSubmit={handleSubmit} data-testid="booking-form">
              <div className="form-group">
                <label><i className="fas fa-user"></i> Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="input-name"
                />
              </div>
              
              <div className="form-group">
                <label><i className="fas fa-map-marker-alt"></i> Address *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  data-testid="input-address"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label><i className="fas fa-phone"></i> Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="input-phone"
                  />
                </div>
                
                <div className="form-group">
                  <label><i className="fas fa-envelope"></i> Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="input-email"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label><i className="fas fa-caravan"></i> Select Trailer *</label>
                <select
                  name="trailer"
                  value={formData.trailer}
                  onChange={handleChange}
                  required
                  data-testid="select-trailer"
                >
                  <option value="starcraft">2013 StarCraft 26BH (#1)</option>
                  <option value="amerilite">2006 Ameri-lite 25BH (#2)</option>
                </select>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label><i className="fas fa-calendar"></i> Start Date *</label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="input-start-date"
                  />
                </div>
                
                <div className="form-group">
                  <label><i className="fas fa-calendar"></i> End Date *</label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    required
                    min={formData.start_date || new Date().toISOString().split('T')[0]}
                    data-testid="input-end-date"
                  />
                </div>
              </div>
              
              <div className="terms-section">
                <h3>Rental Terms Agreement</h3>
                <div className="terms-box">
                  <p><strong>Rental Rates:</strong></p>
                  <ul>
                    <li>Minimum rental: $399 + Tax</li>
                    <li>Extra day: $139</li>
                    <li>Weekly rate: $699</li>
                  </ul>
                  <p><strong>Deposit:</strong> $500 due at reservation (converts to damage deposit at pickup)</p>
                  <p><strong>Pickup Times:</strong> Mon-Thur 12:00-4:00 PM, Friday 12:00-2:00 PM</p>
                  <p><strong>Returns:</strong> Due by 11:00 AM. Late returns: $200 + daily fee</p>
                  <p><strong>Cancellation:</strong> 30+ days: $50 charge | 10-30 days: Loss of deposit | Within 10 days: Full charge</p>
                  <p><strong>Rules:</strong> No smoking, no pets. Camper must be returned clean.</p>
                  <a href="/rental-terms.pdf" target="_blank" className="terms-link">
                    View Full Rental Terms <i className="fas fa-external-link-alt"></i>
                  </a>
                </div>
                
                <div className="signature-section">
                  <label>Digital Signature</label>
                  <canvas
                    ref={canvasRef}
                    width={400}
                    height={150}
                    className="signature-canvas"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    data-testid="signature-canvas"
                  />
                  <button type="button" className="clear-signature" onClick={clearSignature}>
                    <i className="fas fa-eraser"></i> Clear Signature
                  </button>
                </div>
                
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    name="terms_accepted"
                    id="terms_accepted"
                    checked={formData.terms_accepted}
                    onChange={handleChange}
                    data-testid="checkbox-terms"
                  />
                  <label htmlFor="terms_accepted">
                    I have read and agree to the <a href="/rental-terms.pdf" target="_blank">Rental Terms and Conditions</a> *
                  </label>
                </div>
              </div>
              
              <button
                type="submit"
                className="submit-button"
                disabled={loading || !formData.terms_accepted || conflictMessage}
                data-testid="submit-booking"
              >
                {loading ? (
                  <><i className="fas fa-spinner fa-spin"></i> Processing...</>
                ) : (
                  <><i className="fas fa-paper-plane"></i> Submit Booking Request</>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = ({ onBookClick }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [videoDropdownOpen, setVideoDropdownOpen] = useState(false);
  const location = useLocation();

  return (
    <nav className="main-nav" data-testid="main-nav">
      <div className="nav-container">
        <Link to="/" className="nav-logo">
          <i className="fas fa-caravan"></i>
          <span>Shoreline RV Rental</span>
        </Link>
        
        <button className="mobile-menu-toggle" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
          <i className={`fas ${menuOpen ? 'fa-times' : 'fa-bars'}`}></i>
        </button>
        
        <ul className={`nav-links ${menuOpen ? 'active' : ''}`}>
          <li>
            <Link to="/" className={location.pathname === '/' ? 'active' : ''} data-testid="nav-home">
              <i className="fas fa-home"></i> Home
            </Link>
          </li>
          <li>
            <Link to="/trailers" className={location.pathname === '/trailers' ? 'active' : ''} data-testid="nav-trailers">
              <i className="fas fa-caravan"></i> Trailers
            </Link>
          </li>
          <li className="dropdown">
            <button
              className="dropdown-toggle"
              onClick={() => setVideoDropdownOpen(!videoDropdownOpen)}
              data-testid="nav-video-dropdown"
            >
              <i className="fas fa-video"></i> Video Library <i className="fas fa-chevron-down"></i>
            </button>
            <ul className={`dropdown-menu ${videoDropdownOpen ? 'active' : ''}`}>
              <li>
                <a
                  href="https://www.youtube.com/playlist?list=PLp0zguzuPKV7FtLVo6rjObvSIIhAkhzkB"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-starcraft-videos"
                >
                  <i className="fab fa-youtube"></i> StarCraft 26BH Videos
                </a>
              </li>
              <li>
                <a
                  href="https://www.youtube.com/playlist?list=PLp0zguzuPKV61m-K8QZzFucn1FQqaQ8ui"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-amerilite-videos"
                >
                  <i className="fab fa-youtube"></i> Ameri-lite 25BH Videos
                </a>
              </li>
            </ul>
          </li>
          <li>
            <Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''} data-testid="nav-contact">
              <i className="fas fa-phone"></i> Contact
            </Link>
          </li>
          <li>
            <button className="nav-book-btn" onClick={onBookClick} data-testid="nav-book-rental">
              <i className="fas fa-calendar-plus"></i> Book Rental
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
};

// Home Page
const HomePage = ({ onBookClick }) => {
  return (
    <div className="home-page" data-testid="home-page">
      <section className="hero">
        <div className="hero-content">
          <h1>Shoreline RV Rental</h1>
          <p className="tagline">Alexandria, MN's Premier RV Rental Experience</p>
          <p className="hero-subtitle">Explore Minnesota's Lakes Country in Comfort</p>
          <div className="hero-actions">
            <button className="cta-button primary" onClick={onBookClick} data-testid="hero-book-btn">
              <i className="fas fa-calendar-check"></i> Book Your Adventure
            </button>
            <Link to="/trailers" className="cta-button secondary" data-testid="hero-view-trailers">
              <i className="fas fa-caravan"></i> View Our Trailers
            </Link>
          </div>
          <div className="hero-phone">
            <a href="tel:3206346167" data-testid="hero-phone">
              <i className="fas fa-phone-alt"></i> (320) 634-6167
            </a>
          </div>
        </div>
      </section>

      <section className="calendar-section" data-testid="calendar-section">
        <div className="container">
          <h2 className="section-title">
            <i className="fas fa-calendar-alt"></i> Rental Availability Calendar
          </h2>
          <p className="section-subtitle">Check availability and plan your trip</p>
          <div className="calendar-wrapper">
            <iframe
              src="https://calendar.google.com/calendar/embed?src=d0e538cdb4da82376f6de77f08400c9f1abcde52704677ce9f4f0cbc3f61b1ad%40group.calendar.google.com&ctz=America%2FChicago"
              title="Rental Calendar"
              frameBorder="0"
              scrolling="no"
              data-testid="google-calendar"
            ></iframe>
          </div>
        </div>
      </section>

      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Why Choose Shoreline RV?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <i className="fas fa-dollar-sign"></i>
              <h3>Affordable Rates</h3>
              <p>Starting at $399 for minimum rental, $699 weekly</p>
            </div>
            <div className="feature-card">
              <i className="fas fa-bed"></i>
              <h3>Sleep Up To 10</h3>
              <p>Spacious bunk house trailers perfect for families</p>
            </div>
            <div className="feature-card">
              <i className="fas fa-map-marked-alt"></i>
              <h3>Prime Location</h3>
              <p>Located in Alexandria, MN - Gateway to Lakes Country</p>
            </div>
            <div className="feature-card">
              <i className="fas fa-headset"></i>
              <h3>Local Support</h3>
              <p>Friendly local service with how-to video guides</p>
            </div>
          </div>
        </div>
      </section>

      <section className="trailers-preview">
        <div className="container">
          <h2 className="section-title">Our Rental Trailers</h2>
          <div className="trailers-grid">
            <div className="trailer-card">
              <img src="https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?w=400" alt="StarCraft 26BH" />
              <div className="trailer-info">
                <h3>2013 StarCraft 26BH (#1)</h3>
                <p>Sleeps 10 | 29' 4" Length</p>
                <Link to="/trailers" className="view-details">View Details <i className="fas fa-arrow-right"></i></Link>
              </div>
            </div>
            <div className="trailer-card">
              <img src="https://images.unsplash.com/photo-1533745848184-3db07256e163?w=400" alt="Ameri-lite 25BH" />
              <div className="trailer-info">
                <h3>2006 Ameri-lite 25BH (#2)</h3>
                <p>Sleeps 6 | 26' 8" Length</p>
                <Link to="/trailers" className="view-details">View Details <i className="fas fa-arrow-right"></i></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Ready to Start Your Adventure?</h2>
          <p>Book your rental today and explore Minnesota's beautiful lakes country!</p>
          <button className="cta-button primary large" onClick={onBookClick}>
            <i className="fas fa-calendar-check"></i> Reserve Now
          </button>
        </div>
      </section>
    </div>
  );
};

// Trailers Page
const TrailersPage = ({ onBookClick }) => {
  return (
    <div className="trailers-page" data-testid="trailers-page">
      <section className="page-header">
        <h1>Travel Trailer Rental Information for 2025</h1>
        <p>Call Us to Reserve your Rental Today! <a href="tel:3206346167">(320) 634-6167</a></p>
      </section>

      <section className="rental-info">
        <div className="container">
          <div className="info-box">
            <h2>Rental Rates & Policies</h2>
            <ul>
              <li><strong>Minimum rental:</strong> $399 + Tax</li>
              <li><strong>Extra day:</strong> $139</li>
              <li><strong>Weekly rate:</strong> $699</li>
              <li>All rentals to be paid in full at time of camper pickup</li>
              <li><strong>Pick up Time:</strong> Mon-Thur 12:00 - 4:00 PM, Friday 12:00 - 2:00 PM. NO EXCEPTIONS!</li>
              <li>No rental pickups on Saturdays, Sundays or Holidays</li>
              <li>No rentals with return dates on Saturday</li>
              <li>Returns accepted early on Sundays/Holidays - no refunds for early returns</li>
              <li>Returns due by 11:00am on scheduled day of return. Late returns: $200 + daily rental fee</li>
              <li><strong>Deposit:</strong> $500 due at reservation (converts to Damage Deposit at pickup)</li>
              <li>Cash or Credit Cards only for Rental/Damage Deposit</li>
              <li>Rentals overlapping a holiday require Weekly Rental Fee</li>
              <li>Music Festival rentals require Weekly Fee + $1000 CASH Damage Deposit</li>
              <li>Camper must be returned clean, no damage. Smoking/Pets forfeit Damage Deposit</li>
            </ul>

            <h3>Cancellation Policy</h3>
            <ul>
              <li>30 days prior - $50 Charge</li>
              <li>10-30 days prior - Loss of Deposit</li>
              <li>Within 10 Days - Full Rental Charge</li>
            </ul>

            <h3>Vehicle Requirements</h3>
            <ul>
              <li>Six-cylinder or larger engine</li>
              <li>Permanently installed hitch</li>
              <li>2 5/16 inch ball</li>
              <li>Pre-wired for towing</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="trailers-list">
        <div className="container">
          {/* StarCraft Trailer */}
          <div className="trailer-detail" data-testid="starcraft-trailer">
            <h3 className="trailer-title">2013 StarCraft 26BH (#1)</h3>
            
            <PhotoGallery images={STARCRAFT_IMAGES} title="starcraft" />
            
            <div className="video-library-btn">
              <a
                href="https://www.youtube.com/playlist?list=PLp0zguzuPKV7FtLVo6rjObvSIIhAkhzkB"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-video"
                data-testid="starcraft-video-btn"
              >
                <i className="fab fa-youtube"></i> How To Video Library - StarCraft
              </a>
            </div>

            <div className="trailer-specs">
              <div className="spec-group">
                <h4><i className="fas fa-weight-hanging"></i> Weights</h4>
                <ul>
                  <li>Unloaded Vehicle Weight: 4,140 lbs</li>
                  <li>Dry Hitch Weight: 455 lbs</li>
                  <li>Gross Vehicle Weight Rating: 6,500 lbs</li>
                  <li>Cargo Carrying Capacity: 2,360 lbs</li>
                </ul>
              </div>

              <div className="spec-group">
                <h4><i className="fas fa-ruler"></i> Measurements</h4>
                <ul>
                  <li>Exterior Length: 29' 4"</li>
                  <li>Exterior Height w/ AC: 127"</li>
                  <li>Interior Height: 81"</li>
                </ul>
              </div>

              <div className="spec-group">
                <h4><i className="fas fa-tint"></i> Tank Capacities</h4>
                <ul>
                  <li>Fresh Water (incl. water heater): 48 gals</li>
                  <li>Gray Waste Water: 32.5 gals</li>
                  <li>Black Waste Water: 32.5 gals</li>
                </ul>
              </div>

              <div className="spec-group">
                <h4><i className="fas fa-bed"></i> Other</h4>
                <ul>
                  <li>Sleeping Capacity: 10</li>
                  <li>Queen bed, Bunk Beds, sofa bed, dinette bed</li>
                  <li>Absolutely no Pets/Smoking</li>
                  <li>Propane is full - no need to refill</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Ameri-lite Trailer */}
          <div className="trailer-detail" data-testid="amerilite-trailer">
            <h3 className="trailer-title">2006 Ameri-lite 25BH (#2)</h3>
            
            <PhotoGallery images={AMERILITE_IMAGES} title="amerilite" />
            
            <div className="video-library-btn">
              <a
                href="https://www.youtube.com/playlist?list=PLp0zguzuPKV61m-K8QZzFucn1FQqaQ8ui"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-video"
                data-testid="amerilite-video-btn"
              >
                <i className="fab fa-youtube"></i> How To Video Library - Ameri-lite
              </a>
            </div>

            <div className="trailer-specs">
              <div className="spec-group">
                <h4><i className="fas fa-weight-hanging"></i> Weights</h4>
                <ul>
                  <li>Unloaded Vehicle Weight: 3,940 lbs</li>
                  <li>Dry Hitch Weight: 440 lbs</li>
                  <li>Gross Vehicle Weight Rating: 5,920 lbs</li>
                  <li>Cargo Carrying Capacity: 1,524 lbs</li>
                </ul>
              </div>

              <div className="spec-group">
                <h4><i className="fas fa-ruler"></i> Measurements</h4>
                <ul>
                  <li>Exterior Length: 26' 8"</li>
                </ul>
              </div>

              <div className="spec-group">
                <h4><i className="fas fa-tint"></i> Tank Capacities</h4>
                <ul>
                  <li>Fresh Water (incl. water heater): 30 gals</li>
                  <li>Gray Waste Water: 37 gals</li>
                  <li>Black Waste Water: 37 gals</li>
                </ul>
              </div>

              <div className="spec-group">
                <h4><i className="fas fa-bed"></i> Other</h4>
                <ul>
                  <li>Sleeping Capacity: 6</li>
                  <li>Sofa was removed - perfect for Pack-n-Play</li>
                  <li>Absolutely no Pets/Smoking</li>
                  <li>Propane is full - no need to refill</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="book-cta">
        <div className="container">
          <h2>Ready to Book?</h2>
          <button className="cta-button primary large" onClick={onBookClick} data-testid="trailers-book-btn">
            <i className="fas fa-calendar-check"></i> Book Your Rental Now
          </button>
        </div>
      </section>
    </div>
  );
};

// Contact Page
const ContactPage = ({ onBookClick }) => {
  return (
    <div className="contact-page" data-testid="contact-page">
      <section className="page-header">
        <h1>Contact Us</h1>
        <p>We're here to help you plan your perfect camping trip!</p>
      </section>

      <section className="contact-content">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2><i className="fas fa-map-marker-alt"></i> Our Location</h2>
              <address>
                <strong>Shoreline RV</strong><br />
                609 N Nokomis St<br />
                Alexandria, MN 56308
              </address>

              <h3><i className="fas fa-phone-alt"></i> Phone</h3>
              <p><a href="tel:3206346167">(320) 634-6167</a></p>

              <h3><i className="fas fa-envelope"></i> Email</h3>
              <p><a href="mailto:info@shorelinervrental.com">info@shorelinervrental.com</a></p>

              <h3><i className="fas fa-clock"></i> Hours</h3>
              <p>
                Pickup: Mon-Thur 12:00 - 4:00 PM<br />
                Friday: 12:00 - 2:00 PM<br />
                No pickups on weekends or holidays
              </p>

              <button className="cta-button primary" onClick={onBookClick} data-testid="contact-book-btn">
                <i className="fas fa-calendar-check"></i> Book Your Rental
              </button>
            </div>

            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2798.1234567890!2d-95.36672957418314!3d45.89726199338206!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDXCsDUzJzUwLjEiTiA5NcKwMjInMDAuMiJX!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Shoreline RV Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="main-footer" data-testid="main-footer">
      <div className="footer-content">
        <div className="footer-section">
          <h4><i className="fas fa-caravan"></i> Shoreline RV Rental</h4>
          <p>Your trusted RV rental service in Alexandria, Minnesota.</p>
        </div>
        
        <div className="footer-section">
          <h4>Contact</h4>
          <p>609 N Nokomis St<br />Alexandria, MN 56308</p>
          <p><a href="tel:3206346167">(320) 634-6167</a></p>
          <p><a href="mailto:info@shorelinervrental.com">info@shorelinervrental.com</a></p>
        </div>
        
        <div className="footer-section">
          <h4>Quick Links</h4>
          <ul>
            <li><Link to="/">Home</Link></li>
            <li><Link to="/trailers">Trailers</Link></li>
            <li><Link to="/contact">Contact</Link></li>
            <li><a href="/rental-terms.pdf" target="_blank">Rental Terms</a></li>
          </ul>
        </div>
        
        <div className="footer-section">
          <h4>Video Guides</h4>
          <ul>
            <li>
              <a href="https://www.youtube.com/playlist?list=PLp0zguzuPKV7FtLVo6rjObvSIIhAkhzkB" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-youtube"></i> StarCraft How-To
              </a>
            </li>
            <li>
              <a href="https://www.youtube.com/playlist?list=PLp0zguzuPKV61m-K8QZzFucn1FQqaQ8ui" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-youtube"></i> Ameri-lite How-To
              </a>
            </li>
          </ul>
        </div>
      </div>
      
      <div className="footer-bottom">
        <p>&copy; {new Date().getFullYear()} Shoreline RV Rental. All rights reserved.</p>
      </div>
    </footer>
  );
};

// Admin Page for Calendar Authorization
const AdminPage = () => {
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    checkCalendarStatus();
    fetchBookings();
  }, []);

  const checkCalendarStatus = async () => {
    try {
      const response = await axios.get(`${API}/oauth/calendar/status`);
      setCalendarStatus(response.data);
    } catch (error) {
      console.error('Error checking calendar status:', error);
      setCalendarStatus({ is_authorized: false, message: 'Error checking status' });
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const connectCalendar = async () => {
    try {
      const response = await axios.get(`${API}/oauth/calendar/login`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Error initiating OAuth:', error);
      alert('Failed to initiate Google Calendar connection');
    }
  };

  const disconnectCalendar = async () => {
    if (window.confirm('Are you sure you want to disconnect Google Calendar?')) {
      try {
        await axios.post(`${API}/oauth/calendar/disconnect`);
        setCalendarStatus({ is_authorized: false, message: 'Calendar disconnected' });
      } catch (error) {
        console.error('Error disconnecting:', error);
      }
    }
  };

  const cancelBooking = async (bookingId) => {
    if (window.confirm('Are you sure you want to cancel this booking? This will also remove it from the calendar.')) {
      try {
        await axios.delete(`${API}/bookings/${bookingId}`);
        fetchBookings(); // Refresh the list
        alert('Booking cancelled successfully');
      } catch (error) {
        console.error('Error cancelling booking:', error);
        alert('Failed to cancel booking');
      }
    }
  };

  const markDepositReceived = async (bookingId, customerName) => {
    if (window.confirm(`Confirm that you have received the deposit from ${customerName}? This will send a confirmation email to the customer.`)) {
      try {
        await axios.patch(`${API}/bookings/${bookingId}/deposit-paid`);
        fetchBookings(); // Refresh the list
        alert('Deposit marked as received! Confirmation email sent to customer.');
      } catch (error) {
        console.error('Error marking deposit:', error);
        alert('Failed to update booking');
      }
    }
  };

  return (
    <div className="admin-page" data-testid="admin-page">
      <section className="page-header">
        <h1><i className="fas fa-cog"></i> Admin Dashboard</h1>
        <p>Manage your rental bookings and calendar integration</p>
      </section>

      <section className="admin-content">
        <div className="container">
          {/* Calendar Integration Section */}
          <div className="admin-card">
            <h2><i className="fab fa-google"></i> Google Calendar Integration</h2>
            
            {loading ? (
              <p><i className="fas fa-spinner fa-spin"></i> Checking status...</p>
            ) : (
              <div className="calendar-status">
                {calendarStatus?.is_authorized ? (
                  <div className="status-connected">
                    <p><i className="fas fa-check-circle"></i> <strong>Connected</strong></p>
                    <p>Authorized as: {calendarStatus.admin_email}</p>
                    <p className="status-note">New bookings will automatically appear on your Google Calendar.</p>
                    <button className="btn-disconnect" onClick={disconnectCalendar}>
                      <i className="fas fa-unlink"></i> Disconnect Calendar
                    </button>
                  </div>
                ) : (
                  <div className="status-disconnected">
                    <p><i className="fas fa-exclamation-circle"></i> <strong>Not Connected</strong></p>
                    <p>Connect your Google Calendar to automatically add rental bookings.</p>
                    <button className="btn-connect" onClick={connectCalendar}>
                      <i className="fab fa-google"></i> Connect Google Calendar
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bookings Section */}
          <div className="admin-card">
            <h2><i className="fas fa-calendar-alt"></i> Recent Bookings</h2>
            
            {bookings.length === 0 ? (
              <p className="no-bookings">No bookings yet.</p>
            ) : (
              <div className="bookings-table-wrapper">
                <table className="bookings-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Trailer</th>
                      <th>Dates</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => (
                      <tr key={booking.id}>
                        <td>{booking.id.substring(0, 8)}</td>
                        <td>{booking.name}</td>
                        <td>{booking.email}</td>
                        <td>{booking.trailer === 'starcraft' ? 'StarCraft 26BH' : 'Ameri-lite 25BH'}</td>
                        <td>{booking.start_date} to {booking.end_date}</td>
                        <td>
                          <span className={`status-badge status-${booking.status}`}>
                            {booking.status}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            {booking.status === 'pending' && (
                              <button 
                                className="btn-deposit-received"
                                onClick={() => markDepositReceived(booking.id, booking.name)}
                                title="Mark Deposit as Received"
                              >
                                <i className="fas fa-check"></i> Received Deposit
                              </button>
                            )}
                            <button 
                              className="btn-cancel-booking"
                              onClick={() => cancelBooking(booking.id)}
                              title="Cancel Booking"
                            >
                              <i className="fas fa-times"></i> Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

// Main App Component
function App() {
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  const openBookingModal = () => setBookingModalOpen(true);
  const closeBookingModal = () => setBookingModalOpen(false);

  // Check for calendar auth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const calendarAuth = params.get('calendar_auth');
    if (calendarAuth === 'success') {
      alert('Google Calendar connected successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (calendarAuth === 'error') {
      alert('Failed to connect Google Calendar. Please try again.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Navigation onBookClick={openBookingModal} />
        
        <main className="main-content">
          <Routes>
            <Route path="/" element={<HomePage onBookClick={openBookingModal} />} />
            <Route path="/trailers" element={<TrailersPage onBookClick={openBookingModal} />} />
            <Route path="/contact" element={<ContactPage onBookClick={openBookingModal} />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </main>
        
        <Footer />
        
        <BookingModal isOpen={bookingModalOpen} onClose={closeBookingModal} />
        
        {/* Floating Book Button */}
        <button
          className="floating-book-btn"
          onClick={openBookingModal}
          data-testid="floating-book-btn"
        >
          <i className="fas fa-calendar-plus"></i>
          <span>Book Rental</span>
        </button>
      </BrowserRouter>
    </div>
  );
}

export default App;
